import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import { supabase } from '../../lib/supabase';
import {
  getQuickBooksConnection,
  saveQuickBooksConnection,
  setQuickBooksSyncEnabled,
  type QuickBooksConnection,
} from '../../lib/quickbooksSync';

function useQuickBooksConnection(refreshKey = 0) {
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id ?? null;
  const [connection, setConnection] = useState<QuickBooksConnection | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    if (!businessId) {
      setConnection(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      setConnection(await getQuickBooksConnection(businessId));
    } catch {
      setConnection(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void reload(); }, [businessId, refreshKey]);

  return { businessId, connection, loading, reload };
}

export function QuickBooksSyncStatusLabel({ refreshKey = 0 }: { refreshKey?: number }) {
  const { connection, loading } = useQuickBooksConnection(refreshKey);
  if (loading) return <span className="text-xs text-gray-400">Checking</span>;
  if (connection?.sync_enabled && connection.realm_id) {
    return <span className="text-xs font-medium text-green-700">Syncing</span>;
  }
  if (connection?.sync_enabled) {
    return <span className="text-xs font-medium text-amber-600">Waiting for company</span>;
  }
  return <span className="text-xs text-gray-400">Off</span>;
}

function useQuickBooksReturn(openModal: () => void) {
  const openModalRef = useRef(openModal);
  openModalRef.current = openModal;
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const marker = params.get('quickbooks');
    if (marker === 'connected' || marker === 'error') openModalRef.current();
  }, []);
}

export function QuickBooksConnectCard() {
  const [open, setOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  useQuickBooksReturn(() => setOpen(true));
  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen(true); } }}
        className="group bg-white rounded-xl border border-gray-100 px-2.5 py-2 hover:shadow-sm transition-all duration-200 cursor-pointer"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-[#2CA01C] text-white flex items-center justify-center text-sm font-semibold shrink-0">QB</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 truncate leading-tight">QuickBooks</h3>
              <span className="text-[11px] text-gray-400 shrink-0">Accounting</span>
            </div>
            <p className="text-xs text-gray-500 truncate leading-tight mt-0.5">Invoices, payments, and refunds</p>
            <div className="mt-0.5"><QuickBooksSyncStatusLabel refreshKey={refreshKey} /></div>
          </div>
        </div>
      </div>
      <QuickBooksSyncModal open={open} onClose={() => { setOpen(false); setRefreshKey((n) => n + 1); }} />
    </>
  );
}

export function QuickBooksConnectSection() {
  const [open, setOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  useQuickBooksReturn(() => setOpen(true));
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full bg-white rounded-2xl border border-gray-200 px-5 py-4 flex items-center gap-3 text-left hover:bg-gray-50"
      >
        <div className="w-10 h-10 rounded-xl bg-[#2CA01C] text-white flex items-center justify-center text-sm font-semibold shrink-0">QB</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900">QuickBooks</div>
          <div className="text-xs text-gray-500">Sync invoices, payments, and refunds</div>
        </div>
        <QuickBooksSyncStatusLabel refreshKey={refreshKey} />
      </button>
      <QuickBooksSyncModal open={open} onClose={() => { setOpen(false); setRefreshKey((n) => n + 1); }} />
    </>
  );
}

export function QuickBooksSyncModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { businessId, connection, loading, reload } = useQuickBooksConnection();
  const [realmId, setRealmId] = useState('');
  const [currency, setCurrency] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const returned = new URLSearchParams(window.location.search).get('quickbooks');
  const returnedMessage = new URLSearchParams(window.location.search).get('message');

  useEffect(() => {
    setRealmId(connection?.realm_id ?? '');
    setCurrency(connection?.home_currency ?? '');
    setEnabled(connection?.sync_enabled ?? false);
  }, [connection]);

  if (!open) return null;

  const connect = async () => {
    if (!businessId || !supabase) return;
    setConnecting(true);
    setError(null);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('quickbooks-oauth-start', {
        body: {
          businessId,
          returnUrl: `${window.location.origin}/app-store?tab=integrations&quickbooks=connected`,
        },
      });
      if (invokeError) throw invokeError;
      const payload = (data ?? {}) as { authorizeUrl?: string; error?: string };
      if (!payload.authorizeUrl) throw new Error(payload.error || 'QuickBooks did not return a sign-in URL');
      window.location.assign(payload.authorizeUrl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start QuickBooks sign-in');
      setConnecting(false);
    }
  };

  const disconnect = async () => {
    if (!businessId || !supabase) return;
    setConnecting(true);
    setError(null);
    try {
      const { error: invokeError } = await supabase.functions.invoke('quickbooks-oauth-disconnect', {
        body: { businessId },
      });
      if (invokeError) throw invokeError;
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not disconnect QuickBooks');
    } finally {
      setConnecting(false);
    }
  };

  const save = async () => {
    if (!businessId) return;
    setSaving(true);
    setError(null);
    try {
      await saveQuickBooksConnection({
        businessId,
        realmId,
        homeCurrency: currency,
      });
      if (enabled !== (connection?.sync_enabled ?? false)) {
        await setQuickBooksSyncEnabled(businessId, enabled);
      }
      await reload();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save QuickBooks settings');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100050] bg-black/40 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <div className="text-[15px] font-semibold text-gray-900">QuickBooks</div>
            <div className="text-[12px] text-gray-500">Invoices, payments, and refunds</div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {loading ? (
            <div className="text-sm text-gray-400">Loading…</div>
          ) : (
            <>
              <p className="text-[13px] text-gray-600 leading-relaxed">
                Invoices issued after sync is turned on are queued here. QuickBooks receives the copy, including tax, deposits, tips, and refunds. Nothing already on the books is backfilled.
              </p>
              {returned === 'connected' && (
                <p className="text-[12px] text-green-700">QuickBooks is connected. Save if you also want sync on.</p>
              )}
              {returned === 'error' && (
                <p className="text-[12px] text-red-600">{returnedMessage || 'QuickBooks did not connect.'}</p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => void connect()}
                  disabled={connecting || !businessId}
                  className="px-3 py-1.5 rounded-lg bg-[#2CA01C] text-white text-sm font-medium disabled:opacity-50"
                >
                  {connecting ? 'Connecting…' : 'Connect QuickBooks'}
                </button>
                <button
                  type="button"
                  onClick={() => void disconnect()}
                  disabled={connecting || !businessId}
                  className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 disabled:opacity-50"
                >
                  Disconnect
                </button>
              </div>
              <label className="block">
                <span className="text-[12px] font-medium text-gray-700">Company id</span>
                <input
                  value={realmId}
                  onChange={(e) => setRealmId(e.target.value)}
                  placeholder="From the QuickBooks connection"
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-[12px] font-medium text-gray-700">Company currency</span>
                <input
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  placeholder="CAD"
                  maxLength={3}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm uppercase"
                />
              </label>
              <label className="flex items-center justify-between gap-3">
                <span className="text-sm text-gray-800">Sync invoices from now on</span>
                <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} className="h-4 w-4" />
              </label>
              {connection?.connected_at && (
                <p className="text-[12px] text-gray-500">
                  Started {new Date(connection.connected_at).toLocaleString()}
                </p>
              )}
              {enabled && !realmId.trim() && (
                <p className="text-[12px] text-amber-700">The company id is still empty, so the queue will wait until the QuickBooks connection is finished.</p>
              )}
              {error && <p className="text-[12px] text-red-600">{error}</p>}
            </>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex justify-end">
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving || loading || !businessId}
            className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
